<template>
  <div class="cost-audit-body">
    <div class="remark-section">
      <div class="remark-label">
        需要成本确认
      </div>
    </div>
    <template v-if="!form.no_need_confirm">
      <div v-for="(category, cIndex) in costMap"
           :key="category.name"
           class="category-card"
           :class="`theme-${category.theme}`">
        <div class="card-header">
          <div class="title-wrap">
            <i :class="category.icon"
               class="header-icon"/>
            <span class="category-name">{{ category.name }}</span>
            <span class="badge">{{ category.items.length }}/10</span>
          </div>
        </div>
        <div class="card-body">
          <div v-if="category.items.length > 0"
               class="item-list">
            <div v-for="(item, iIndex) in category.items"
                 :key="iIndex"
                 class="cost-item-row"
                 :class="{ 'is-abnormal': item._abnormal }">
              <el-checkbox v-model="item._abnormal"
                           class="abnormal-check"/>
              <div class="name-input">
                <el-input v-model="item.cost_name"
                          :readonly="true"/>
              </div>
              <div class="amount-input">
                <el-input v-model="item.cost_amount"
                          :readonly="true">
                  <template slot="append">{{ item.cost_unit || '元/家' }}</template>
                </el-input>
              </div>
            </div>
          </div>
          <div v-else
               class="empty-state">
            <p>暂无成本明细</p>
          </div>
        </div>
      </div>
    </template>

    <div class="remark-section">
      <div class="remark-label">
        备注 <span class="sub-text">（非必填）</span>
      </div>
      <el-input v-model="form.remarks"
                type="textarea"
                :rows="3"
                :readonly="true"/>
    </div>

    <div class="audit-conclusion">
      <div class="conclusion-title">审批结论</div>
      <div class="conclusion-options">
        <label class="option-radio"
               :class="{ active: auditAction === 'pass' }">
          <input type="radio"
                 name="audit_action"
                 value="pass"
                 v-model="auditAction"/>
          <span>审批通过</span>
          <span class="option-desc">成本合理,同意执行</span>
        </label>
        <label class="option-radio option-reject"
               :class="{ active: auditAction === 'reject' }">
          <input type="radio"
                 name="audit_action"
                 value="reject"
                 v-model="auditAction"/>
          <span>审批驳回</span>
          <span class="option-desc">成本存在问题,需重新核算</span>
        </label>
      </div>
    </div>
    <div v-if="auditAction === 'reject'"
         class="reject-reason">
      <div class="reject-label">驳回原因（非必填）</div>
      <el-input v-model="rejectReason"
                type="textarea"
                :rows="3"
                placeholder="请输入驳回原因..."/>
      <div class="reject-tip">驳回时建议填写具体原因,审批通过则无需填写</div>
    </div>
    <!-- 底部操作 -->
    <div class="dialog-footer">
      <el-button @click="closeDialog">取 消</el-button>
      <el-button type="primary"
                 @click="onSubmit">审批完成
      </el-button>
    </div>
  </div>
</template>

<script>
import projectMixins from '@views/project/project.mixins'

export default {
  mixins: [projectMixins],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: [String, Number],
      default: '',
    },
    fullNode: {
      type: Object,
      default: () => ({}),
    },
    customerId: {
      type: [String, Number],
      default: '',
    },
  },
  data () {
    return {
      costMap: [
        {name: '前端成本', theme: 'blue', icon: 'el-icon-monitor', items: []},
        {name: '后端成本', theme: 'green', icon: 'el-icon-data-line', items: []},
        {name: '其他成本', theme: 'orange', icon: 'el-icon-menu', items: []},
      ],
      form: {
        no_need_confirm: false,
        assign_project_id: null,
        remarks: null,
      },
      auditAction: '', // 审批结论：pass / reject
      rejectReason: '',
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      },
    },
  },
  watch: {
    fullNode: {
      handler (val) {
        if (val) {
          this.initAttr(_.get(val, 'project_node', {}) || {})
        }
      },
      deep: true,
    },
  },
  created () {
    if (this.fullNode) {
      this.initAttr(_.get(this.fullNode, 'project_node', {}) || {})
    }
  },
  methods: {
    closeDialog () {
      this.dialogVisible = false
    },
    getCostFieldKey (category) {
      const map = {
        '前端成本': 'front_cost_details',
        '后端成本': 'backend_cost_details',
        '其他成本': 'other_cost_details',
      }
      return map[category.name]
    },
    // 回显已有数据到表单
    initAttr (projectNode) {
      const nodeResult = _.get(projectNode, 'node_result', {})

      this.costMap.forEach((category) => {
        const fieldKey = this.getCostFieldKey(category)
        if (fieldKey && Array.isArray(nodeResult[fieldKey])) {
          category.items = _.cloneDeep(nodeResult[fieldKey]).map(item => ({
            ...item,
            _abnormal: item.item_audit_status === 'reject',
          }))
        } else {
          category.items = []
        }
      })

      const notConfirm = _.get(nodeResult, 'no_need_confirm', false)
      const assignPId = notConfirm ? _.get(nodeResult, 'assign_project_id', null) : null
      this.form = {
        no_need_confirm: notConfirm,
        assign_project_id: assignPId,
        remarks: _.get(nodeResult, 'remarks', ''),
      }
      this.auditAction = ''
      this.rejectReason = ''
    },
    // 提交审批结果
    async onSubmit () {
      if (!this.auditAction) {
        this.$message.error('请先选择审批结论（通过或驳回）')
        return
      }

      const params = {
        id: this.projectId,
        no_need_confirm: this.form.no_need_confirm,
        assign_project_id: this.form.assign_project_id,
        remarks: this.form.remarks,
        audit_action: this.auditAction,
        reject_reason: this.auditAction === 'reject' ? this.rejectReason.trim() : '',
      }

      this.costMap.forEach((category) => {
        const fieldKey = this.getCostFieldKey(category)
        if (fieldKey) {
          params[fieldKey] = (category.items || []).map(item => {
            const {_abnormal, ...rest} = item
            return {
              ...rest,
              item_audit_status: _abnormal ? 'reject' : (item.item_audit_status || ''),
            }
          })
        }
      })

      try {
        await this.$confirm(
          `确定${this.auditAction === 'pass' ? '审批通过' : '审批驳回'}？提交后将无法撤销。`,
          '提示',
          {confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'}
        )
      } catch (e) {
        return
      }

      await this.withLoading(async () => {
        try {
          if (this.auditAction === 'pass') {
            await request.common('auditPassProjectCostApi', params)
          } else {
            await request.common('auditRejectProjectCostApi', params)
          }
          this.closeDialog()
          this.$message.success(this.auditAction === 'pass' ? '审批通过成功' : '已驳回')
        } catch (error) {
          this.$message.error(error.message || '提交失败')
        }
      })
    },
  },
}
</script>

<style lang="scss"
       scoped>
.cost-audit-body {
  padding: 0 4px;
}

/* 动态分类卡片基类 */
.category-card {
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fafafa;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-name {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
}

.badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

/* 主题颜色样式 */
.theme-blue {
  .header-icon {
    color: #2f54eb;
  }

  .badge {
    background-color: #e6f7ff;
    color: #1890ff;
  }
}

.theme-green {
  .header-icon {
    color: #52c41a;
  }

  .badge {
    background-color: #f6ffed;
    color: #52c41a;
  }
}

.theme-orange {
  .header-icon {
    color: #fa8c16;
  }

  .badge {
    background-color: #fff7e6;
    color: #fa8c16;
  }
}

/* 条目编辑行 */
.cost-item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  transition: background-color 0.2s;

  &.is-abnormal {
    background-color: #fef0f0;
    border: 1px solid #f56c6c;
    border-left: 4px solid #f56c6c;
    border-radius: 4px;
    padding: 6px 10px;
    margin-left: -10px;

    .name-input .el-input__inner,
    .amount-input .el-input__inner {
      border-color: #f56c6c;
      color: #f56c6c;
      background-color: #fff5f5;
      font-weight: 600;
    }
  }
}

.abnormal-check {
  flex-shrink: 0;
}

.name-input {
  flex: 2;
}

.amount-input {
  flex: 1;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 18px 0;
  color: #909399;
  font-size: 13px;
}
/* 备注 */
.remark-section {
  margin-bottom: 20px;

  .remark-label {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .sub-text {
    font-weight: normal;
    color: #909399;
  }
}

/* 审批结论 */
.audit-conclusion {
  margin-bottom: 20px;
}

.conclusion-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.conclusion-options {
  display: flex;
  gap: 24px;
}

.option-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 14px 20px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.2s;
  position: relative;

  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid #dcdfe6;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;

    &:checked {
      border-color: #409eff;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #409eff;
      }
    }
  }

  span:first-of-type {
    font-weight: 600;
    font-size: 15px;
    color: #303133;
  }

  .option-desc {
    font-size: 12px;
    color: #909399;
  }

  &.active {
    border-color: #409eff;
    background-color: #ecf5ff;
  }

  &.option-reject.active {
    border-color: #f56c6c;
    background-color: #fef0f0;

    input[type="radio"]:checked {
      border-color: #f56c6c;
    }

    input[type="radio"]:checked::after {
      background: #f56c6c;
    }
  }
}

/* 驳回原因 */
.reject-reason {
  margin-bottom: 20px;

  .reject-label {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .reject-tip {
    margin-top: 6px;
    font-size: 12px;
    color: #909399;
  }
}

/* 底部按钮 */
.dialog-footer {
  margin-top: 24px;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/views/project/node-audit/ProjectCostAuditDialog.vue